/**
 * PDP "Free Gift" card (blocks/product-gift-offer.liquid).
 *
 * BOGOS puts every live offer in window.FGSECOMAPP.offers. An offer is shown here when one of its
 * conditions is product based and targets this product:
 *   - product_narrow type "variants_ids": value is keyed by product id
 *   - product_narrow type "product_collections": value is keyed by collection id
 *     (filter_option "exclude" flips it)
 * Bag-total offers (unit "money" with no product_narrow) apply to every product, so they show on
 * every PDP as "Free gift over <amount>". Offers whose name contains one of the block's exclude
 * keywords (default "sachets") are never shown. Offers that give the same gift(s) are shown once
 * (e.g. several "maraya-lip-charm" offers all giving the same charm).
 *
 * The note under the card matches what assets/bogos-auto-gift.js does in the bag: offers with a
 * single gift product are added automatically, offers with a choice of products are picked by
 * the shopper.
 */
const WAIT_MS = 10000;
const POLL_MS = 200;
const MOBILE_QUERY = '(max-width: 749px)'; // same breakpoint custom.css uses to hide .products-criteria
const MAX_NAMED = 3;
const GIFT_ICON =
  '<svg class="gift-card__icon" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C9.5 3 12 8 12 8s2.5-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';
const CHECK_ICON =
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9.5"/><path d="m8 12.3 2.7 2.7L16.2 9.5"/></svg>'; // name up to 3 products / collections in the badge, else "eligible products"

class ProductGiftOffer extends HTMLElement {
  connectedCallback() {
    if (this.#moving) return; // just relocated by #place: keep running as before
    this.#waitForBogos();
    // BOGOS fires this when its app has finished rendering; re-check in case offers arrived late.
    document.addEventListener('fg-app:end', this.#onBogosReady);
    this.#mobile = window.matchMedia(MOBILE_QUERY);
    this.#mobile.addEventListener('change', this.#place);
    // Some browsers / emulators skip the media-query change event, so also re-check on resize.
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.#place();
  }

  /*
   * On mobile the theme hides the whole "All our products are" section (.products-criteria), so a
   * card placed there would vanish. On mobile the card moves itself under the product accordion
   * (Ingredients is its last row), or under Add to bag when there is none; back on
   * desktop it returns to where it was placed in the editor (a comment marks that spot).
   */
  #mobile = null;
  #home = null;
  #moving = false;

  #resizeFrame = 0;

  #onResize = () => {
    clearTimeout(this.#resizeFrame);
    this.#resizeFrame = window.setTimeout(this.#place, 120);
  };

  #place = () => {
    if (!this.#home) {
      if (!this.closest('.products-criteria')) return; // already somewhere visible on mobile
      this.#home = document.createComment('product-gift-offer');
      this.before(this.#home);
      this.dataset.giftOfferHome = 'criteria'; // still "the criteria copy" after moving on mobile
    }

    this.#moving = true;
    try {
      if (this.#mobile?.matches) {
        // Under the Description / How to use / Ingredients accordion; under Add to bag if the
        // product has no accordion.
        const target =
          document.querySelector('.product-information .product-details .accordion') ||
          document.querySelector('.product-information .accordion') ||
          document.querySelector('.product-information .buy-buttons-block');
        if (target && target.nextElementSibling !== this) target.after(this);
      } else if (this.#home.isConnected && this.#home.nextSibling !== this) {
        this.#home.after(this);
      }
    } finally {
      this.#moving = false;
    }
  };

  disconnectedCallback() {
    if (this.#moving) return;
    this.#mobile?.removeEventListener('change', this.#place);
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#resizeFrame);
    clearTimeout(this.#timer);
    document.removeEventListener('fg-app:end', this.#onBogosReady);
    this.#galleryObserver?.disconnect();
    this.#galleryObserver = null;
    this.#removeImageLabel();
  }

  #timer = 0;

  #onBogosReady = () => {
    const offers = window.FGSECOMAPP?.offers;
    if (Array.isArray(offers) && offers.length) this.#render(offers);
  };

  /**
   * BOGOS creates FGSECOMAPP.offers as an empty array first and fills it a moment later, so wait
   * until it has entries (poll briefly, then give up quietly: no offers means no card).
   */
  #waitForBogos(startedAt = Date.now()) {
    const offers = window.FGSECOMAPP?.offers;
    if (Array.isArray(offers) && offers.length) {
      this.#render(offers);
      return;
    }
    if (Date.now() - startedAt > WAIT_MS) return;
    this.#timer = window.setTimeout(() => this.#waitForBogos(startedAt), POLL_MS);
  }

  get #strings() {
    const el = this.querySelector('template[data-strings]')?.content.firstElementChild;
    return el instanceof HTMLElement ? el.dataset : {};
  }

  /** @param {any} narrow one product_narrow entry; returns true/false, or null if not product based */
  #narrowMatches(narrow) {
    const productId = String(this.dataset.productId || '');
    const value = narrow?.value && typeof narrow.value === 'object' ? narrow.value : null;
    if (!value) return null;

    if (narrow.type === 'variants_ids') return Object.prototype.hasOwnProperty.call(value, productId);

    if (narrow.type === 'product_collections') {
      if (this.dataset.showCollectionOffers === 'false') return false;
      const ids = String(this.dataset.collectionIds || '').split(',').filter(Boolean);
      const inCollection = ids.some((id) => Object.prototype.hasOwnProperty.call(value, id));
      return narrow.filter_option === 'exclude' ? !inCollection : inCollection;
    }
    return null;
  }

  /**
   * Offers never shown on the PDP: IDs listed in "Hide offer IDs", or names containing one of the
   * "Hide offers named" keywords (e.g. "sachets").
   */
  #isExcluded(offer) {
    // Offer IDs listed in the block's "Hide offer IDs" setting (numbers from the BOGOS dashboard).
    const ids = String(this.dataset.excludeIds || '')
      .split(/[\s,]+/)
      .map((id) => id.replace(/\D/g, ''))
      .filter(Boolean);
    if (ids.includes(String(offer?.id ?? '').replace(/\D/g, ''))) return true;

    const keywords = String(this.dataset.excludeKeywords || '')
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);
    if (!keywords.length) return false;
    const names = `${offer?.title || ''} ${offer?.offer_name || ''}`.toLowerCase();
    return keywords.some((k) => names.includes(k));
  }

  /**
   * How this product can unlock the offer, or null if it can't:
   *   { kind: 'count', min }  buy at least `min` items (product / collection offers)
   *   { kind: 'spend', min }  bag total of at least `min` (base currency). A spend condition with no
   *                           product list is a whole-bag offer, so it matches every product.
   * When an offer has several conditions, the easiest one wins (count before spend).
   */
  #matchOffer(offer) {
    const now = Date.now();
    if (offer?.deleted_at) return null;
    if (offer?.start_time && Date.parse(offer.start_time) > now) return null;
    if (offer?.end_time && Date.parse(offer.end_time) <= now) return null;
    if (!Array.isArray(offer?.gift_products) || !offer.gift_products.length) return null;
    if (this.#isExcluded(offer)) return null;

    let best = null;
    for (const condition of offer.offer_conditions || []) {
      const narrows = condition?.product_narrow || [];
      const isSpend = condition?.unit === 'money';

      let matched = null;
      if (narrows.length) {
        matched = narrows.find((n) => this.#narrowMatches(n) === true);
        if (!matched) continue;
      } else if (!isSpend || this.dataset.showSpendOffers === 'false') {
        continue; // no product list: only whole-bag spend offers apply to every product
      }

      const match = isSpend
        ? { kind: 'spend', min: Math.max(0, Number(condition.min_amount) || 0) }
        : {
            kind: 'count',
            min: Math.max(1, Math.ceil(Number(condition.min_amount) || 1)),
            scope: this.#scopeOf(matched),
          };

      if (
        !best ||
        (match.kind === 'count' && best.kind === 'spend') ||
        (match.kind === best.kind && match.min < best.min)
      ) {
        best = match;
      }
    }
    return best ? { offer, ...best } : null;
  }

  /**
   * What counts towards a "buy N" condition, read from BOGOS's own data, for the badge text:
   *   { type: 'collection', names: ['Skincare'] }         product_narrow "product_collections"
   *   { type: 'products', names: [...], onlyThis: bool }  product_narrow "variants_ids"
   * null when it can't be named simply (e.g. an "exclude collection" rule): the badge then says
   * "eligible products".
   */
  #scopeOf(narrow) {
    const value = narrow?.value && typeof narrow.value === 'object' ? narrow.value : null;
    if (!value) return null;

    const local = this.#localNames;

    if (narrow.type === 'product_collections') {
      if (narrow.filter_option === 'exclude') return null;
      // Use the translated collection title when this product is in that collection.
      const names = Object.entries(value)
        .map(([id, title]) => String(local.collections?.[id] || title || '').trim())
        .filter(Boolean);
      return names.length ? { type: 'collection', names } : null;
    }

    if (narrow.type === 'variants_ids') {
      const productId = String(this.dataset.productId || '');
      const ids = Object.keys(value);
      // This product's own name comes translated from Shopify; other products keep BOGOS's name.
      const nameOf = (id) => String((id === productId && local.product) || value[id]?.title || '').trim();
      const names = [...new Set(ids.map(nameOf).filter(Boolean))];
      if (!names.length) return null;
      // Other products only have BOGOS's English title; on Arabic pages #localizeCondition swaps
      // it for the product's Arabic name (same gift-name lookup as the gift title).
      const lookups = ids
        .filter((id) => id !== productId && value[id]?.handle)
        .map((id) => ({ name: nameOf(id), handle: value[id].handle }));
      return {
        type: 'products',
        names,
        lookups,
        onlyThis: ids.length === 1 && ids[0] === productId,
        includesThis: ids.includes(productId),
        thisName: ids.includes(productId) ? nameOf(productId) : '',
      };
    }
    return null;
  }

  /**
   * With several cards for the same product, the one placed under "All our products are"
   * (.products-criteria, still marked after it moves on mobile) wins; otherwise the first one
   * on the page.
   */
  #isPrimary() {
    const same = [...document.querySelectorAll('product-gift-offer')].filter(
      (el) => el.dataset.productId === this.dataset.productId
    );
    if (same.length < 2) return true;
    const inCriteria = (el) => !!el.closest('.products-criteria') || el.dataset.giftOfferHome === 'criteria';
    return (same.find(inCriteria) || same[0]) === this;
  }

  #mergeScopes(a, b) {
    if (!a || !b || a.type !== b.type) return null; // mixed rules: fall back to "eligible products"
    const merged = { ...a, names: [...new Set([...a.names, ...b.names])] };
    if (a.type === 'products') {
      merged.onlyThis = a.onlyThis && b.onlyThis;
      merged.includesThis = a.includesThis && b.includesThis;
      merged.thisName = a.thisName || b.thisName;
      merged.lookups = [...(a.lookups || []), ...(b.lookups || [])];
    }
    return merged;
  }

  /** { product, collections: { id: title } } in the page's language, from the Liquid block. */
  get #localNames() {
    if (this.#localCache) return this.#localCache;
    try {
      this.#localCache = JSON.parse(this.querySelector('script[data-local-names]')?.textContent || '{}');
    } catch {
      this.#localCache = {};
    }
    return this.#localCache;
  }

  #localCache = null;

  /** "A", "A or B", "A, B or C" with the translated "or". */
  #joinNames(names, s) {
    const or = ` ${s.or || 'or'} `;
    if (names.length <= 1) return names[0] || '';
    return `${names.slice(0, -1).join(', ')}${or}${names[names.length - 1]}`;
  }

  /**
   * Condition shown on the right of the pink header, e.g. "With any 2 Skincare products".
   * Empty for buy-1 offers (the header already says "Free gift"). Up to MAX_NAMED products /
   * collections are named; more than that reads "any N eligible products".
   */
  #conditionText(min, scope, s) {
    const fill = (template, extra = {}) =>
      String(template || '')
        .replace('{count}', String(min))
        .replace('{collection}', extra.collection || '')
        .replace('{products}', extra.products || '')
        .replace('{product}', extra.product || '');

    if (scope?.type === 'collection' && scope.names.length <= MAX_NAMED) {
      return fill(s.condCollection || 'With any {count} {collection} products', {
        collection: this.#joinNames(scope.names, s),
      });
    }
    if (min <= 1) return '';
    if (scope?.type === 'products' && scope.onlyThis) {
      return fill(s.condThis || 'When you buy {count} {product}', { product: scope.names[0] });
    }
    if (scope?.type === 'products' && scope.names.length <= MAX_NAMED) {
      return fill(s.condProducts || 'With any {count} of {products}', { products: this.#joinNames(scope.names, s) });
    }
    if (scope?.type === 'products' && scope.includesThis && scope.thisName) {
      return fill(s.condIncluding || 'When you buy {count} including {product}', { product: scope.thisName });
    }
    return fill(s.condMin || 'With any {count} eligible products');
  }


  #amount(value) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }

  #cleanTitle(title) {
    return String(title || '')
      .replace(/🎁/gu, '')
      .replace(/\(\s*\d+%\s*off\s*\)/i, '')
      .trim();
  }

  /** @param {any[]} offers */
  #render(offers) {
    const matches = offers.map((o) => this.#matchOffer(o)).filter(Boolean);
    // Product offers first (fewest items first), then bag-total offers (lowest amount first).
    const kindOrder = { count: 0, spend: 1 };
    matches.sort((a, b) => kindOrder[a.kind] - kindOrder[b.kind] || a.min - b.min);

    // One entry per distinct gift (product + first variant); the first (easiest) requirement wins.
    /** @type {Map<string, { gift: any, variant: any, kind: string, min: number, scope: any, choice: boolean }>} */
    const gifts = new Map();
    for (const { offer, kind, min, scope } of matches) {
      const choice = new Set(offer.gift_products.map((g) => g.original_product_shopify_id)).size > 1;
      for (const gift of offer.gift_products) {
        const variant = (gift.variants || [])[0] || {};
        const key = `${gift.original_product_shopify_id}:${variant.original_variant_shopify_id || ''}`;
        const existing = gifts.get(key);
        if (!existing) {
          gifts.set(key, { gift, variant, kind, min, scope, choice });
        } else if (existing.kind === kind && existing.min === min) {
          // Same gift from several offers with the same requirement (e.g. the Maraya charm offers,
          // each pairing Maraya Lip Shine with another lip product): name everything that counts.
          existing.scope = this.#mergeScopes(existing.scope, scope);
        }
      }
    }

    // Only one card per product page: if the block was added twice (e.g. under Add to bag AND
    // under "All our products are"), the extra copy stays hidden and leaves the label to the other.
    if (!this.#isPrimary()) {
      this.hidden = true;
      return;
    }

    const list = this.querySelector('[data-list]');
    if (!list || !gifts.size) {
      this.hidden = true;
      this.#removeImageLabel();
      return;
    }

    const s = this.#strings;
    list.replaceChildren(...[...gifts.values()].map((entry) => this.#item(entry, s)));
    if (this.hasAttribute('data-arabic')) this.#localizeGiftNames(list);


    this.hidden = false;
    this.#ensureImageLabel();
    this.#watchGallery();
  }

  /*
   * "Free gift" label on the product image, next to NEW / BESTSELLER. It lives in the same
   * .product-tag-labels--pdp-media container (created when the product has no other label) and
   * scrolls to this card on click. Only added while the card is visible.
   */
  #galleryObserver = null;

  get #gallery() {
    // Same section first; the card can also sit in a separate section below the product
    // (e.g. under "All our products are"), so fall back to the page's product gallery.
    const scope = this.closest('.product-information, .shopify-section');
    return scope?.querySelector('media-gallery') || document.querySelector('.product-information media-gallery, media-gallery');
  }

  #ensureImageLabel() {
    if (this.hidden) return;
    const gallery = this.#gallery;
    if (!gallery || gallery.querySelector('[data-gift-offer-label]')) return;

    let labels = gallery.querySelector(':scope > .product-tag-labels--pdp-media');
    if (!labels) {
      labels = document.createElement('div');
      labels.className = 'product-tag-labels product-tag-labels--pdp-media';
      gallery.append(labels);
    }

    const label = document.createElement('button');
    label.type = 'button';
    label.className = 'product-tag-labels__label product-tag-labels__label--gift';
    label.dataset.giftOfferLabel = '';
    label.textContent = this.#strings.badge || 'Free Gift';
    label.addEventListener('click', () => this.#scrollToCard());
    labels.prepend(label);
  }

  #removeImageLabel() {
    this.#gallery?.querySelectorAll('[data-gift-offer-label]').forEach((el) => el.remove());
  }

  /** The gallery can be re-rendered (e.g. on variant change); put the label back when it is. */
  #watchGallery() {
    if (this.#galleryObserver) return;
    const target = this.#gallery?.parentElement;
    if (!target) return;
    let queued = false;
    this.#galleryObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.#ensureImageLabel();
      });
    });
    this.#galleryObserver.observe(target, { childList: true, subtree: true });
  }

  #scrollFrame = 0;

  /**
   * Eased scroll to the card (the browser's own smooth scroll is short and can feel like a jump).
   * Centres the card in the space below the sticky header; jumps straight there for shoppers who
   * prefer reduced motion. Any wheel / touch / key input by the shopper cancels it.
   */
  #scrollToCard() {
    const header = document.querySelector('#header-component, .header-section, header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const rect = this.getBoundingClientRect();
    const space = window.innerHeight - headerBottom;
    const offset = rect.height < space ? (space - rect.height) / 2 : 16;
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    const target = Math.min(maxY, Math.max(0, window.scrollY + rect.top - headerBottom - offset));

    const highlight = () => {
      this.classList.remove('gift-offer--flash');
      void this.offsetWidth; // restart the highlight animation
      this.classList.add('gift-offer--flash');
    };

    cancelAnimationFrame(this.#scrollFrame);
    const root = document.documentElement;
    const startY = window.scrollY;
    const distance = target - startY;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(distance) < 2) {
      window.scrollTo(0, target);
      highlight();
      return;
    }

    // The theme sets `scroll-behavior: smooth` on <html>, which would fight a frame-by-frame scroll.
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    const duration = Math.min(1000, Math.max(500, Math.abs(distance) * 0.6));
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2); // easeInOutCubic
    const startTime = performance.now();
    const events = ['wheel', 'touchstart', 'keydown'];

    const finish = () => {
      events.forEach((type) => window.removeEventListener(type, stop));
      root.style.scrollBehavior = previousBehavior;
    };
    const stop = () => {
      cancelAnimationFrame(this.#scrollFrame);
      finish();
    };
    events.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, startY + distance * ease(t));
      if (t < 1) {
        this.#scrollFrame = requestAnimationFrame(step);
      } else {
        finish();
        highlight();
      }
    };
    this.#scrollFrame = requestAnimationFrame(step);
  }

  /**
   * Arabic pages: BOGOS only has English gift titles, so swap each title for the gift product's
   * Arabic short name (variant custom.short_name_ar, same as the PDP title). Fetched from the
   * theme's product.gift-name template and cached for the session; keeps the English title if
   * the lookup fails or there is no Arabic name.
   */
  #localizeGiftNames(list) {
    for (const titleEl of list.querySelectorAll('[data-gift-handle]')) {
      const { giftHandle: handle, giftVariant: variantId } = titleEl.dataset;
      if (!handle) continue;
      ProductGiftOffer.giftName(handle, variantId).then((name) => {
        if (name && titleEl.isConnected) titleEl.textContent = name;
      });
    }
  }

  /**
   * Arabic pages: the header condition lists other products by their English BOGOS title
   * ("... Maraya Lip Shine أو ليجاسي ليبستيك"). Look up each one's Arabic name (variant
   * custom.short_name_ar, else the translated title) and redraw the line once they arrive.
   */
  #localizeCondition(condition, min, scope, s) {
    const lookups = scope?.type === 'products' ? scope.lookups || [] : [];
    if (!lookups.length) return;
    Promise.all(lookups.map(({ handle }) => ProductGiftOffer.giftName(handle))).then((arabic) => {
      const map = new Map();
      lookups.forEach(({ name }, i) => {
        if (arabic[i]) map.set(name, arabic[i]);
      });
      if (!map.size || !condition.isConnected) return;
      const names = [...new Set(scope.names.map((n) => map.get(n) || n))];
      const text = this.#conditionText(min, { ...scope, names }, s);
      if (text) condition.textContent = text;
    });
  }

  static #nameRequests = new Map();

  static giftName(handle, variantId) {
    const root = window.Shopify?.routes?.root || '/';
    const key = `${root}|${handle}|${variantId || ''}`;
    const storageKey = `asteri-gift-name:${key}`;
    try {
      const cached = sessionStorage.getItem(storageKey);
      if (cached) return Promise.resolve(cached);
    } catch {
      // sessionStorage unavailable: just fetch
    }
    if (!ProductGiftOffer.#nameRequests.has(key)) {
      const url = `${root}products/${encodeURIComponent(handle)}?view=gift-name${variantId ? `&variant=${encodeURIComponent(variantId)}` : ''}`;
      // No "Accept: application/json" header: with it Shopify returns its own product JSON and
      // ignores ?view=gift-name, so the Arabic name never arrives.
      const request = fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((text) => {
          // The template returns JSON; anything else (e.g. template not uploaded) is ignored.
          let name = '';
          try {
            name = String(JSON.parse(text)?.name || '').trim();
          } catch {
            name = '';
          }
          try {
            // Only cache a real name, so a failed lookup is retried on the next page.
            if (name) sessionStorage.setItem(storageKey, name);
          } catch {
            // ignore
          }
          return name;
        })
        .catch(() => '');
      ProductGiftOffer.#nameRequests.set(key, request);
    }
    return ProductGiftOffer.#nameRequests.get(key);
  }

  /**
   * One ticket-style card per gift:
   *   [pink header]  gift icon  FREE GIFT ............ condition ("Over 400.00", "With any 2 ...")
   *   [body]         framed image | title, shade pill, Worth ~~price~~  [FREE]
   *   - - - - - - - - (dashed tear line, see-through notches each side) - - - - - - - -
   *   [footer]       check icon | "Added to your bag automatically ..."
   */
  #item({ gift, variant, kind, min, scope, choice }, s) {
    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    };
    const moneySpan = (value) => {
      // Bare base-currency number inside .money so the currency switcher converts it.
      const price = el('span', 'price');
      price.append(el('span', 'money', this.#amount(value)));
      return price;
    };

    const card = el('div', 'gift-card');

    // Header
    const head = el('div', 'gift-card__head');
    const label = el('span', 'gift-card__label');
    label.innerHTML = GIFT_ICON;
    label.append(el('span', null, s.badge || 'Free Gift'));
    head.append(label);

    const condition = el('span', 'gift-card__condition');
    if (kind === 'spend' && min > 0) {
      const [before, after = ''] = String(s.condSpend || 'Over {amount}').split('{amount}');
      condition.append(before, moneySpan(min), after);
    } else {
      condition.textContent = this.#conditionText(min, scope, s);
      if (this.hasAttribute('data-arabic')) this.#localizeCondition(condition, min, scope, s);
    }
    if (condition.textContent.trim()) head.append(condition);
    // Top half of the ticket (header + gift); the bottom half holds the note.
    const top = el('div', 'gift-card__top');
    top.append(head);

    // Body
    const body = el('div', 'gift-card__body');
    const image = variant.thumbnail || gift.thumbnail;
    if (image) {
      const media = el('div', 'gift-card__media');
      const img = el('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 120;
      img.height = 120;
      media.append(img);
      body.append(media);
    }

    const info = el('div', 'gift-card__info');
    // div, not p: the theme forces its sans font on <p> with !important.
    const titleText = this.#cleanTitle(gift.title || variant.gift_product?.title);
    const title = el('div', 'gift-card__title', titleText);
    // Used on Arabic pages to look up the gift's Arabic short name.
    if (gift.original_product_handle) title.dataset.giftHandle = gift.original_product_handle;
    if (variant.original_variant_shopify_id) title.dataset.giftVariant = String(variant.original_variant_shopify_id);
    info.append(title);

    const variantTitle = String(variant.variant_title || '').trim();
    // Skip "Default Title" and BOGOS clone titles that list several shades ("A, B, C").
    if (
      variantTitle &&
      !/^default title$/i.test(variantTitle) &&
      !variantTitle.includes(',') &&
      variantTitle.toLowerCase() !== titleText.toLowerCase()
    ) {
      // "Shade 030" for plain shade codes, the variant title as-is otherwise ("cream Sachet").
      const shadeLabel = /^\d+$/.test(variantTitle) && s.shade ? s.shade.replace('{shade}', variantTitle) : variantTitle;
      info.append(el('span', 'gift-card__variant', shadeLabel));
    }

    const price = el('div', 'gift-card__price');
    // "Worth" = the gift's own compare-at or price in Shopify; hidden when that is 0 (e.g. a gift-only item).
    const worthValue = Math.max(Number(variant.original_compare_at_price) || 0, Number(variant.original_price) || 0);
    if (worthValue > 0) {
      const worth = el('span', 'gift-card__worth', `${s.worth || 'Worth'} `);
      const was = el('s');
      was.append(moneySpan(worthValue));
      worth.append(was);
      price.append(worth);
    }
    price.append(el('span', 'gift-card__free', s.free || 'Free'));
    info.append(price);
    body.append(info);
    top.append(body);

    // Bottom half: dashed tear line (its ::before) + note. The side notches are real cut-outs:
    // each half is masked with a half circle at its corners on the tear line.
    const foot = el('div', 'gift-card__foot');
    const check = el('span', 'gift-card__check');
    check.innerHTML = CHECK_ICON;
    let note = s.auto || '';
    if (choice) note = s.choose || note;
    else if (kind === 'spend' || min > 1) note = s.autoMin || note;
    foot.append(check, el('span', 'gift-card__note', note));
    card.append(top, foot);

    return card;
  }
}

if (!customElements.get('product-gift-offer')) customElements.define('product-gift-offer', ProductGiftOffer);